// Every environment with its state and limits (the existing list endpoints).
import { Cube } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import type { Environment, Organisation } from "../api/client";
import { environmentsQuery } from "../api/queries";
import { WidgetCard } from "../ui/Card";
import { EmptyState, ErrorState, Loading } from "../ui/states";
import { DotStatus, Mono } from "../ui/status";
import { environmentTone, statusLabel } from "../ui/tones";
import { gibOf } from "./format";
import { useT } from "../i18n";

function Figure({ value, label }: { value: string | number; label: string }) {
  return (
    <span>
      <span className="tabular block text-[13px] font-semibold text-gray-900">{value}</span>
      {label}
    </span>
  );
}

function EnvironmentRow({ env, org }: { env: Environment; org: Organisation | undefined }) {
  const t = useT().dashboard;
  return (
    <Link
      to="/environments"
      className="block rounded-lg border border-muted p-3.5 transition-colors hover:border-primary/40 hover:bg-gray-50 dark:hover:bg-gray-100/40"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-semibold text-gray-900">
            {org ? `${org.name} / ` : ""}
            {env.name}
          </div>
          <Mono className="block truncate text-[11.5px] text-gray-500">
            org-{org?.slug ?? "?"}/env-{env.slug}
          </Mono>
        </div>
        <DotStatus tone={environmentTone(env.status)} label={<span className="capitalize">{statusLabel(env.status)}</span>} pulse={env.status === "ready"} />
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-gray-500">
        <Figure value={env.limits_cpu} label={t.capacity.cpu} />
        <Figure value={gibOf(env.limits_memory_bytes)} label="GiB" />
        <Figure value={env.egress_allowlist.length} label={t.environments.egressRules} />
      </div>
    </Link>
  );
}

export function EnvironmentsWidget({ orgs, className }: { orgs: Organisation[]; className?: string }) {
  const t = useT().dashboard.environments;
  const query = useQuery(environmentsQuery());
  return (
    <WidgetCard
      className={className}
      title={t.title}
      action={
        <Link to="/environments" className="text-[13px] font-medium text-primary">
          {t.all}
        </Link>
      }
    >
      {query.isPending ? <Loading /> : null}
      {query.isError ? <ErrorState message={query.error.message} /> : null}
      {query.data?.length === 0 ? (
        <EmptyState compact icon={<Cube weight="duotone" />} title={t.none}>
          {t.noneBody}
        </EmptyState>
      ) : null}
      <div className="space-y-3">
        {query.data?.slice(0, 4).map((env) => (
          <EnvironmentRow key={env.id} env={env} org={orgs.find((o) => o.id === env.org_id)} />
        ))}
      </div>
    </WidgetCard>
  );
}
